import React, { useState } from 'react';
import PropTypes from 'prop-types';

const DAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();

// Returns 0=Mon ... 6=Sun for the first day of month
const getFirstDayOfMonth = (year, month) => {
  const day = new Date(year, month, 1).getDay(); // 0=Sun
  return day === 0 ? 6 : day - 1; // convert to Mon-based
};

const isSameDay = (a, b) =>
  a && b && a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const isBetween = (date, start, end) => {
  if (!start || !end) return false;
  const d = date.getTime();
  return d > start.getTime() && d < end.getTime();
};

const DateRangePicker = ({ onClose, onApply }) => {
  const today = new Date();
  const [viewYear, setViewYear]   = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate]     = useState(null);
  const [hovering, setHovering]   = useState(null);

  const daysInMonth  = getDaysInMonth(viewYear, viewMonth);
  const firstDayIdx  = getFirstDayOfMonth(viewYear, viewMonth);

  // Previous month fill
  const prevMonthDays = getDaysInMonth(
    viewMonth === 0 ? viewYear - 1 : viewYear,
    viewMonth === 0 ? 11 : viewMonth - 1,
  );

  const handleDayClick = day => {
    const clicked = new Date(viewYear, viewMonth, day);
    if (!startDate || (startDate && endDate)) {
      setStartDate(clicked);
      setEndDate(null);
    } else {
      if (clicked < startDate) {
        setEndDate(startDate);
        setStartDate(clicked);
      } else {
        setEndDate(clicked);
      }
    }
  };

  const handleApply = () => {
    if (startDate && endDate) onApply({ startDate, endDate });
  };

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };

  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };

  // Build calendar grid: 6 rows × 7 cols
  const cells = [];
  // Leading days from prev month
  for (let i = firstDayIdx - 1; i >= 0; i--) {
    cells.push({ day: prevMonthDays - i, current: false });
  }
  // Current month days
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ day: d, current: true });
  }
  // Trailing days from next month
  const trailing = 42 - cells.length;
  for (let d = 1; d <= trailing; d++) {
    cells.push({ day: d, current: false });
  }

  const effectiveEnd = endDate || hovering;

  return (
    /* Backdrop */
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Card */}
      <div style={{ background: 'white', borderRadius: '20px', padding: '20px', width: '340px', boxShadow: '0 16px 48px rgba(0,0,0,0.15)', fontFamily: "'Segoe UI', sans-serif", position: 'relative' }}>

        {/* Close button */}
        <button
          onClick={onClose}
          style={{ position: 'absolute', top: 16, right: 16, width: 36, height: 36, borderRadius: '10px', border: '1.5px solid #e53935', background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#e53935', fontSize: 18, fontWeight: 700 }}
        >
          ×
        </button>

        {/* Month / Year header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '18px' }}>
          <button onClick={prevMonth} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#888', padding: '0 4px' }}>‹</button>
          <span style={{ fontWeight: 700, fontSize: 16, color: '#1a1a1a' }}>
            {MONTHS[viewMonth]} {viewYear}
          </span>
          <button onClick={nextMonth} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#888', padding: '0 4px' }}>›</button>
        </div>

        {/* Day headers */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: '6px' }}>
          {DAYS.map(d => (
            <div key={d} style={{ textAlign: 'center', fontSize: 11, fontWeight: 600, color: '#aaa', paddingBottom: 4 }}>{d}</div>
          ))}
        </div>

        {/* Calendar grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
          {cells.map((cell, idx) => {
            if (!cell.current) {
              return (
                <div key={`nc-${idx}`} style={{ textAlign: 'center', padding: '8px 0', fontSize: 13, color: '#ddd' }}>
                  {cell.day}
                </div>
              );
            }

            const cellDate  = new Date(viewYear, viewMonth, cell.day);
            const isStart   = isSameDay(cellDate, startDate);
            const isEnd     = isSameDay(cellDate, endDate);
            const isToday   = isSameDay(cellDate, today);
            const inRange   = startDate && isBetween(cellDate, startDate, effectiveEnd && effectiveEnd > startDate ? effectiveEnd : null);
            const isHoverEnd = isSameDay(cellDate, hovering) && !endDate;

            let bg     = 'transparent';
            let color  = '#1a1a1a';
            let radius = '50%';
            let fontW  = 400;

            if (isStart || isEnd) {
              bg    = '#D4001D';
              color = 'white';
              fontW = 700;
            } else if (inRange || isHoverEnd) {
              bg     = '#fde8e8';
              color  = '#D4001D';
              radius = '0';
            } else if (isToday) {
              color = '#D4001D';
              fontW = 700;
            }

            return (
              <div
                key={`c-${cell.day}`}
                onClick={() => handleDayClick(cell.day)}
                onMouseEnter={() => startDate && !endDate && setHovering(cellDate)}
                onMouseLeave={() => setHovering(null)}
                style={{
                  textAlign: 'center', padding: '8px 0', fontSize: 13,
                  background: bg, color, borderRadius: radius,
                  fontWeight: fontW, cursor: 'pointer',
                  transition: 'background 0.1s',
                  userSelect: 'none',
                }}
              >
                {cell.day}
              </div>
            );
          })}
        </div>

        {/* Done button */}
        <button
          onClick={handleApply}
          disabled={!startDate || !endDate}
          style={{
            marginTop: 20, width: '100%', padding: '13px',
            borderRadius: '12px', border: '1.5px solid #D4001D',
            background: 'white', color: '#D4001D',
            fontSize: 15, fontWeight: 700, cursor: startDate && endDate ? 'pointer' : 'not-allowed',
            opacity: startDate && endDate ? 1 : 0.5,
            transition: 'background 0.15s, color 0.15s',
          }}
          onMouseEnter={e => { if (startDate && endDate) { e.currentTarget.style.background = '#D4001D'; e.currentTarget.style.color = 'white'; } }}
          onMouseLeave={e => { e.currentTarget.style.background = 'white'; e.currentTarget.style.color = '#D4001D'; }}
        >
          Done
        </button>
      </div>
    </div>
  );
};

DateRangePicker.propTypes = {
  onClose: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
};

export default DateRangePicker;